/**
 * Which fakeable state a screen is showing, read from the URL.
 *
 * Screen spec 1.3a: every state is deep linkable as ?state=oauth-denied. That
 * is not a debug affordance, it is what makes a review possible. A reviewer
 * opens two states side by side in two tabs, a screenshot of a failure state
 * is a link rather than a description, and nobody has to click a screen into
 * the shape they want to talk about.
 *
 * In mock builds the state lives in the URL and nowhere else, so it survives
 * a refresh and loses nothing when the demo store is reset. Real builds have
 * no such deep-link contract, so they track state with plain React state.
 */

import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

export interface ScreenState<S extends string> {
  /** The state named in the URL (mock builds), or plain component state (real builds). */
  state: S;
  /** Replaces the state in place. No history entry: this is not navigation. */
  setState: (next: S) => void;
}

export function useScreenState<S extends string>(states: readonly S[], fallback: S): ScreenState<S> {
  const location = useLocation();
  const navigate = useNavigate();
  const [realState, setRealState] = useState<S>(fallback);

  // ?state= is a design-review affordance for the mock build only. Faking a
  // state (over-quota, upload failure, ...) against a real signed-in
  // account's data would be a screen lying about itself in production.
  const raw = USE_MOCK ? new URLSearchParams(location.search).get('state') : null;
  const urlState = states.includes(raw as S) ? (raw as S) : fallback;

  const setState = useCallback(
    (next: S) => {
      if (!USE_MOCK) {
        setRealState(next);
        return;
      }
      const params = new URLSearchParams(location.search);
      if (next === fallback) params.delete('state');
      else params.set('state', next);
      const query = params.toString();
      navigate(query ? `${location.pathname}?${query}` : location.pathname, { replace: true });
    },
    [fallback, location.pathname, location.search, navigate],
  );

  return { state: USE_MOCK ? urlState : realState, setState };
}
